import React, { forwardRef, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import 'focus-visible';
import { Icon } from '../icon';
import styles from './styles/base.module.css';
/**
 * `<Checkbox />` Компонент позволяющий пользователю управлять параметром с двумя состояниями — включено и отключено.
 *
 * @example ``` tsx
 *  <Checkbox onChange={() => {}} checked />
 * ```
 */

export const Checkbox = /*#__PURE__*/forwardRef(function Checkbox({
  className = '',
  onChange,
  checked,
  label,
  indeterminate,
  withExtraClickZone,
  size = 'm',
  variant = 'base',
  disabled,
  ...props
}, ref) {
  const checkboxRootClass = useMemo(() => clsx(styles.checkbox, styles[size], styles[variant], {
    [styles.disabled]: disabled,
    [styles.extraClickZone]: withExtraClickZone,
    [styles.indeterminate]: indeterminate
  }, className), [size, className, withExtraClickZone, disabled, indeterminate, variant]);
  const iconSize = useMemo(() => {
    switch (size) {
      case 'm':
        if (indeterminate) {
          return 10;
        }

        return 12;

      case 'l':
        if (indeterminate) {
          return 12;
        }

        return 13;

      default:
        return 12;
    }
  }, [size, indeterminate]);
  const onChangeHandler = useCallback(event => {
    const value = event.target.checked;

    if (onChange) {
      onChange(value);
    }
  }, [onChange]);
  return /*#__PURE__*/React.createElement("label", {
    className: checkboxRootClass,
    "data-testid": "checkbox"
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.inputContainer
  }, /*#__PURE__*/React.createElement("input", { ...props,
    ref: ref,
    className: styles.inputInner,
    type: "checkbox",
    checked: checked,
    onChange: onChangeHandler,
    disabled: disabled,
    "data-testid": "checkbox-input"
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.checkboxIcon
  }, /*#__PURE__*/React.createElement(Icon, {
    size: iconSize
  }, indeterminate ? /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("rect", {
    width: "24",
    height: "4",
    x: "0",
    y: "10"
  })) : /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 13 12",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M1.8 5.30002L0 7.10002L4.3 11.4L13 1.80002L11 0L4.1 7.60002L1.8 5.30002Z"
  }))))), label && /*#__PURE__*/React.createElement("span", {
    className: styles.labelText,
    "data-testid": "checkbox-label"
  }, label));
});